<template>
  <v-dialog
    :model-value="show"
    :width="modalWidth"
    :fullscreen="fullscreen"
    :persistent="disableClose"
    @update:model-value="handleAction('close')"
  >
    <template #activator="{ props }">
      <slot
        name="activator"
        v-bind="props"
      />
    </template>

    <v-sheet
      rounded="lg"
    >
      <div
        class="header d-flex justify-space-between pa-6 pb-0"
        :class="{ divider: withDivider }"
      >
        <h5>
          <slot name="title" />
        </h5>

        <v-btn
          class="ml-2 text-textDarkest opacity-70"
          variant="text"
          size="sm"
          min-width="24"
          min-height="24"
          :disabled="disableClose"
          @click="handleAction('close')"
        >
          <DpplSvg file-name="icon-close" />
        </v-btn>
      </div>

      <div
        class="modal-content-wrapper pr-6 pl-6"
      >
        <div
          class="content mt-4"
        >
          <slot name="content" />
        </div>
      </div>

      <div
        v-if="!hideDefaultActions"
        class="actions d-flex pr-6 pl-6 mt-4"
      >
        <slot name="actions">
          <a
            v-if="cancelText"
            class="text-error text-button cursor-pointer"
            :disabled="disabled"
            data-testid="cancel-text"
            @click="handleAction('close')"
          >
            {{ cancelText }}
          </a>
          <a
            v-if="clearText"
            :class="['text-error text-button cursor-pointer', { disabled }]"
            :disabled="disabled"
            data-testid="clear-text"
            @click="handleAction('clear')"
          >
            {{ clearText }}
          </a>
          <v-spacer />
          <v-btn
            v-if="actionText"
            :class="{ 'w-100': !cancelText && !clearText }"
            variant="flat"
            rounded="lg"
            :disabled="disabled"
            :loading="modalLoading"
            data-testid="action-text"
            color="primary"
            v-bind="buttonProps"
            @click="handleAction('action')"
          >
            {{ actionText }}
          </v-btn>
        </slot>
      </div>

      <slot name="footer" />
      <Alert
        :show="showError"
        @close="handleAction('closeError')"
      >
        <slot name="error" />
      </Alert>

      <div class="pa-6 pt-0" />
    </v-sheet>
  </v-dialog>
</template>

<script>
import Alert from '@/components/shared/Alert.vue';
import DpplSvg from '@/components/datapeople/DpplSvg.vue';
import { mapState } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useColors } from '@/composables/colors';

export default {
  name: 'DpplModal',
  components: {
    DpplSvg,
    Alert,
  },
  props: {
    show: { type: Boolean, default: false },
    showError: { type: Boolean, default: false },
    cancelText: { type: String, default: 'Cancel' },
    clearText: { type: [String, Boolean], default: '' },
    actionText: { type: [String, Boolean], default: 'Action' },
    buttonProps: { type: Object, default: () => ({}) },
    width: { type: String, default: 'small' },
    fullscreen: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    disableClose: { type: Boolean, default: false },
    hideDefaultActions: { type: Boolean, default: false },
    withDivider: { type: Boolean, default: false },
  },
  emits: ['close', 'clear', 'action', 'closeError'],
  setup() {
    const { brandPrimary } = useColors();

    return { brandPrimary };
  },
  data() {
    return {
      activated: false,
    };
  },
  computed: {
    ...mapState(useAppLoading, ['modalLoading']),
    modalWidth() {
      if (this.fullscreen) {
        return undefined;
      }

      if (this.width === 'small') {
        return '350';
      }
      if (this.width === 'medium') {
        return '380';
      }
      if (this.width === 'large') {
        return '638';
      }

      return this.width;
    },
  },
  methods: {
    handleAction(action) {
      if (action === 'close' && this.disableClose) return;
      this.$emit(action);
    },
  },
};
</script>
<style scoped lang="less">

:deep(.v-dialog) {
  overflow-y: unset !important;
}
.disabled {
  pointer-events: none;
  opacity: 0.4;
}

.header {
  h5 {
    color: rgb(var(--v-theme-textDarkest));
    line-height: 24px;
  }

  &.divider {
    border-bottom: 1px solid rgb(var(--v-theme-strokeDefault));
    padding-bottom: 16px !important;
  }
}

.content {
  color: rgb(var(--v-theme-textDarkest), 70%);
  font-size: 13px;
  font-weight: 500;
}

.actions .text-button {
  letter-spacing: normal !important;
  text-transform: capitalize !important;
}
</style>
